/**
 * Persists the invite deep-link token (`?invite_token=`) across the login /
 * signup boundary (KIR-3786).
 *
 * A first-time invitee lands on `/?invite_token=<id>` while unauthenticated, but
 * `InviteGuard` only mounts once authenticated — and Descope's signup / email
 * verification redirect returns to the bare origin, dropping the query param.
 * Without persistence the invite is silently lost and the user is never added to
 * the org. We stash the token in `localStorage` (shared across tabs, so a
 * magic-link opened in a new tab still finds it) the moment the app first loads,
 * and read it back as a fallback after auth completes.
 */
const INVITE_TOKEN_STORAGE_KEY = 'kirin_pending_invite_token';

/**
 * If the current URL carries an `invite_token`, stash it for later. Safe to call
 * on every app mount; a no-op when the param is absent.
 */
export function captureInviteTokenFromUrl(): void {
  try {
    const token = new URLSearchParams(window.location.search).get(
      'invite_token'
    );
    if (token) {
      localStorage.setItem(INVITE_TOKEN_STORAGE_KEY, token);
    }
  } catch {
    // localStorage can throw (private mode / disabled storage); the URL param
    // still works for the same-tab case, so swallow and continue.
  }
}

/**
 * Resolve the active invite token: the live URL param takes precedence, falling
 * back to the persisted value from a pre-auth load.
 */
export function getPersistedInviteToken(): string | null {
  const fromUrl = new URLSearchParams(window.location.search).get(
    'invite_token'
  );
  if (fromUrl) {
    return fromUrl;
  }
  try {
    return localStorage.getItem(INVITE_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Strip `invite_token` from the address bar without a navigation.
 *
 * Clearing `localStorage` alone is not enough to stop the invite modal:
 * `getPersistedInviteToken` reads the **URL first**, and the user arrives from
 * the email link on `/?invite_token=…`. Leave the param in place and any reload
 * re-opens the same modal, which is exactly the "blocked on every page" symptom
 * (KIR-4166). `replaceState` keeps the current history entry so Back still works
 * and nothing re-renders.
 */
function stripInviteTokenFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('invite_token')) {
      return;
    }
    url.searchParams.delete('invite_token');
    window.history.replaceState(
      {},
      '',
      `${url.pathname}${url.search}${url.hash}`
    );
  } catch {
    // Non-fatal: the localStorage copy is still cleared below/above, and a
    // stale param only costs one extra dismiss.
  }
}

/**
 * Forget the pending invite in this browser — both the stashed copy and the
 * `?invite_token=` in the URL — once it has been handled (accepted, rejected,
 * dismissed, or found unanswerable) so it never re-triggers.
 *
 * This is purely local: it does **not** touch the invite server-side, so the
 * emailed link stays valid for whoever it was actually issued to (KIR-4166).
 */
export function clearPersistedInviteToken(): void {
  try {
    localStorage.removeItem(INVITE_TOKEN_STORAGE_KEY);
  } catch {
    // Ignore storage errors — nothing to clean up if it never persisted.
  }
  stripInviteTokenFromUrl();
}
